import { observer } from 'mobx-react';
import { useCallback, useMemo } from 'react';
import { useCheckoutStoreContext } from '../../context';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { defaultBranch } from 'src/constants/branches';
import { DELIVERY_TYPE } from 'src/constants/deliveryTypes';
import theme from 'src/constants/styles';
import { ICheckoutDependencies } from 'src/mobx/checkoutStore/types';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { IBranch } from 'src/types/branches';
import { getTranslate } from 'src/utils/helpers';
import Icon from 'src/view/components/Controls/Icon/Icon';
import FakePicker from 'src/view/components/Controls/Pickers/FakePicker/FakePicker';
import Text, { TEXT } from 'src/view/components/Text/Text';
import './style.scss';

const CheckoutBranches: React.FC = observer((): JSX.Element => {
  const { store: checkoutStore } = useCheckoutStoreContext();

  const onChangeBranch = useCallback(
    (branch: IBranch | undefined, location: ICheckoutDependencies['location'], delivery_type: DELIVERY_TYPE) => {
      checkoutStore.selectBranch(branch || defaultBranch);
      checkoutStore.selectLocation(location);

      if (delivery_type === DELIVERY_TYPE.DINE_IN) {
        checkoutStore.changeDeliveryType(DELIVERY_TYPE.PICKUP);
        checkoutStore.toggleDineIn(1);
        checkoutStore.toggleCurbside(0);
        checkoutStore.defineDinein(checkoutStore.dependencies.branch);
      } else {
        checkoutStore.changeDeliveryType(delivery_type);
        checkoutStore.toggleDineIn(0);
      }
    },
    [checkoutStore.dependencies.branch]
  );

  const openDeliveryTypeModal = useCallback(() => {
    modalStore.openDeliveryType({
      editable: checkoutStore.isDraft,
      location: checkoutStore.dependencies.location,
      customer: checkoutStore.dependencies.customer?.id,
      branch: checkoutStore.dependencies.branch,
      delivery_type: checkoutStore.params.is_dinein
        ? DELIVERY_TYPE.DINE_IN
        : checkoutStore.params.delivery_type === 'p'
        ? DELIVERY_TYPE.PICKUP
        : DELIVERY_TYPE.DELIVERY,
      onChange: onChangeBranch,
    });
  }, [
    checkoutStore.isDraft,
    checkoutStore.dependencies.location,
    checkoutStore.dependencies.customer,
    checkoutStore.dependencies.branch,
    checkoutStore.params.is_dinein,
    checkoutStore.params.delivery_type,
  ]);

  const branchName = useMemo((): string => {
    return getTranslate(checkoutStore.dependencies.branch.name);
  }, [checkoutStore.dependencies.branch]);

  return (
    <div className="d-flex align-items-center mb-4">
      <Text type={TEXT.ORDER_FIELD} text="Branch" required />
      {checkoutStore.isDraft ? (
        <FakePicker
          onClick={openDeliveryTypeModal}
          error={checkoutStore.validatorErrors.branch}
          disabled={!checkoutStore.isDraft}
          placeholder={localeStore.t('select_branch')}
          className="flex-grow-1 px-2"
          wrapperClassName="checkout-branches"
          textClassName="family-medium"
          placeholderClassName="family-medium"
          value={branchName}
          prevComponent={<Icon name={Icons.BranchesTree} size={18} color={theme.colors.mainblue} className="mr-3" />}
        />
      ) : (
        <p className="family-medium color-dark text-size-16 font-weight-500">{branchName}</p>
      )}
    </div>
  );
});

export default CheckoutBranches;
